import { sum } from "lodash";
import { call, put, select, takeEvery } from "redux-saga/effects";
import { v4 as uuidv4 } from "uuid";

import {
  SaleLotAction,
  ScanAction,
  WeighLotScanAction,
  patchSaleLot,
  patchScan,
  setSaleLotOfflineValues,
} from "actions";

import { SCAN, WEIGH_LOT_SCAN } from "constants/actionTypes";
import { UNALLOCATED } from "constants/scanner";
import { WeighLotScanStatus } from "constants/weighScanning";

import { calculateUnitPrice, calculateUnitPriceDollars } from "lib";
import { calculateTotalPriceCents } from "lib/pricing";

import { getLivestockSaleId } from "lib/navigation";

import {
  getSaleLotById,
  getSaleLots,
  getScanByEid,
  getScansBySaleLotId,
  getWeighLotScanById,
  getWeighLotScanIdsByEid,
  getWeighLotScans,
} from "selectors";

// Keep and ignore for Weigh Lot Scans are handled entirely server-side (see
// `WeighLotScanAction.keep`/`.ignore`) - the backend decides reweigh vs mapped status, ignores
// siblings sharing the same EID, relinks/recalculates the associated Scan's weight, and returns
// every Weigh Lot Scan/Scan it touched, which the reducers merge directly on `.SUCCESS`. No saga
// is needed here for that path.

function* onHandleAddScanToSale(action) {
  const { weighLotScanId } = action;

  const state = yield select();

  const weighLotScan = getWeighLotScanById(weighLotScanId)(state);

  const { eid, totalMassGrams } = weighLotScan;

  yield put(
    ScanAction.create([
      {
        id: uuidv4(),
        livestock_sale_id: getLivestockSaleId(),
        EID: eid,
        weigh_lot_scan_id: weighLotScanId,
        total_mass_grams: totalMassGrams,
        recalculate_salelot_weight: true,
      },
    ]),
  );
  yield put(
    WeighLotScanAction.update({
      id: weighLotScanId,
      status: WeighLotScanStatus.MAPPED,
    }),
  );
}

function* onHandleManuallyMapToScan(action) {
  const { weighLotScanId, scan } = action;

  const state = yield select();

  const weighLotScan = getWeighLotScanById(weighLotScanId)(state);

  const weighLotScanByIdLookup = getWeighLotScans(state);

  // we want to get the asssociated weigh lot scan ids that have the same eid as the selected scan
  const weighLotScanIdsSharingSameEid = getWeighLotScanIdsByEid(scan.EID)(
    state,
  );
  const ignorableWeighLotScanIds = weighLotScanIdsSharingSameEid.filter(
    weighLotScanId =>
      weighLotScanId !== weighLotScan.id &&
      weighLotScanByIdLookup[weighLotScanId]?.status !==
        WeighLotScanStatus.DEDUPLICATED,
  );

  // if the scan already has a weigh lot scan - set that weigh lot scan as ignored
  if (scan.weigh_lot_scan_id && scan.weigh_lot_scan_id !== weighLotScanId) {
    yield put(
      WeighLotScanAction.update({
        id: scan.weigh_lot_scan_id,
        status: WeighLotScanStatus.IGNORED,
      }),
    );
  }
  // add the new weight to the scan and link the select weigh lot scan
  yield put(
    patchScan(scan, {
      weigh_lot_scan_id: weighLotScanId,
      total_mass_grams: weighLotScan.totalMassGrams,
      recalculate_salelot_weight: true,
      sale_lot_id: scan.sale_lot_id !== UNALLOCATED ? scan.sale_lot_id : null,
    }),
  );
  // updated the selected weigh lot scan status to manually mapped
  yield put(
    WeighLotScanAction.update({
      id: weighLotScanId,
      status: WeighLotScanStatus.MANUALLY_MAPPED,
    }),
  );

  // set asssociated weigh lot scan ids that have the same eid as the selected scan as ignored
  for (const id of ignorableWeighLotScanIds) {
    yield put(
      WeighLotScanAction.update({
        id,
        status: WeighLotScanStatus.IGNORED,
      }),
    );
  }
}

function* onHandleAddWeightToSaleLot(action) {
  const { weighLotScanId, saleLotId } = action;

  const state = yield select();

  const saleLot = getSaleLots(state)[saleLotId];

  const weighLotScan = getWeighLotScans(state)[weighLotScanId];

  const newPrice = calculateTotalPriceCents({
    total_mass_grams: weighLotScan.totalMassGrams,
    pricing_type_id: saleLot.pricing_type_id,
    quantity: saleLot.quantity,
    unitPrice: calculateUnitPriceDollars(saleLot),
  });

  const comment = `This Sale Lot weight has been overwritten by a weigh lot scan with the EID: ${weighLotScan.eid}.\n\nThe price has been updated from ${saleLot.total_price_cents} to ${newPrice}. \n\nThe Weight has been updated from ${saleLot.total_mass_grams} to ${weighLotScan.totalMassGrams}`;

  yield put(
    patchSaleLot({
      ...saleLot,
      total_mass_grams: weighLotScan.totalMassGrams,
      total_price_cents: newPrice,
    }),
  );

  yield put(
    WeighLotScanAction.update({
      id: weighLotScanId,
      status: WeighLotScanStatus.MAPPED_TO_SALE_LOT,
    }),
  );

  yield put(SaleLotAction.comment(saleLotId, { comment }));
}

function* calculateOfflineSaleLotWeightValues(saleLotIds) {
  // for each sale lot get the total weight of scans associated to the sale lot
  // and apply that weight to the sale lot while updating the price
  const state = yield select();
  for (const saleLotId of saleLotIds) {
    const saleLot = getSaleLotById(saleLotId)(state);
    // saleLotId may be UNALLOCATED :sadparrot
    if (saleLot) {
      const saleLotScans = getScansBySaleLotId(saleLotId)(state);

      const adjustedSaleLotWeight = sum(
        saleLotScans.map(scan =>
          scan.total_mass_grams ? parseInt(scan.total_mass_grams, 10) : 0,
        ),
      );

      yield put(
        setSaleLotOfflineValues(saleLot.id, {
          total_mass_grams: adjustedSaleLotWeight,
          total_price_cents: calculateTotalPriceCents({
            ...saleLot,
            unitPrice: calculateUnitPrice(saleLot),
            total_mass_grams: adjustedSaleLotWeight,
          }),
        }),
      );
    }
  }
}

function* handleBulkOfflineAllocation(action) {
  // if we pass recalculate_salelot_weight into the scans payload, we want to calculate the
  // weight of all the scans associated the each sale lot affected and apply that weight to
  // to each sale lot
  const { payload: scansList } = action;

  if (!Array.isArray(scansList) || !scansList.length) {
    return;
  }

  const state = yield select();

  const saleLotIdsToUpdate = new Set();

  // build a list of affected sale lots - this includes the new sale lot and stored sale lots
  if (scansList.length) {
    const firstScan = scansList[0];
    if (firstScan.recalculate_salelot_weight) {
      for (const scan of scansList) {
        const oldScan = getScanByEid(scan.EID)(state) || {};
        saleLotIdsToUpdate.add(scan.sale_lot_id || null);
        saleLotIdsToUpdate.add(oldScan.sale_lot_id || null);
      }
    }

    saleLotIdsToUpdate.delete(null);

    if (saleLotIdsToUpdate.size) {
      yield call(calculateOfflineSaleLotWeightValues, saleLotIdsToUpdate);
    }
  }
}

function* handleSingleOfflineAllocation(action) {
  // if we pass recalculate_salelot_weight into a scan payload, we want to calculate the
  // weight of all the scans associated the each sale lot affected and apply that weight to
  // to each sale lot
  const {
    meta: { payload: scan },
  } = action;

  if (scan) {
    if (scan.recalculate_salelot_weight && scan.sale_lot_id) {
      yield call(calculateOfflineSaleLotWeightValues, [scan.sale_lot_id]);
    }
  }
}

export default function* weighLotScanSaga() {
  yield takeEvery(
    WEIGH_LOT_SCAN.ADD_SCAN_TO_SALE.ACTION,
    onHandleAddScanToSale,
  );
  yield takeEvery(
    WEIGH_LOT_SCAN.MANUALLY_MAP_TO_SCAN.ACTION,
    onHandleManuallyMapToScan,
  );
  yield takeEvery(
    WEIGH_LOT_SCAN.ADD_WEIGH_TO_SALE_LOT.ACTION,
    onHandleAddWeightToSaleLot,
  );
  yield takeEvery(SCAN.CREATE.REQUEST, handleBulkOfflineAllocation);
  yield takeEvery(SCAN.UPDATE.REQUEST, handleSingleOfflineAllocation);
}
